import { subject } from "@casl/ability";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Button,
    Skeleton,
    Widget,
    WidgetActions,
    WidgetContent,
    WidgetHeader,
    WidgetTitle,
} from "frontend-components";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Can } from "@dashboard/casl/taskAbility";
import { useAnnouncements } from "@dashboard/hooks/useAnnouncements";
import { useHseInstance } from "@dashboard/hooks/useHseInstance";
import { AddEditAnnouncementModal } from "./AddEditAnnouncementModal";
import { AnnouncementItem } from "./AnnouncementItem";
import { ViewAnnouncementModal } from "./ViewAnnouncementModal";

export default function Announcements() {
    const { t } = useTranslation();
    const { instanceId } = useHseInstance();
    const { data, isPending, isError } = useAnnouncements();
    const [showAdd, setShowAdd] = useState(false);
    const [selectedAnnouncementId, setSelectedAnnouncementId] = useState<number | null>(null);

    const announcements = data?.data ?? [];
    const selectedAnnouncement =
        selectedAnnouncementId === null
            ? null
            : (announcements.find((a) => a.id === selectedAnnouncementId) ?? null);

    return (
        <>
            <Widget>
                <WidgetHeader>
                    <WidgetTitle>{t("common.announcements")}</WidgetTitle>
                    <WidgetActions>
                        <Can I="create" this={subject("Announcement", { instanceId })}>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => setShowAdd(true)}
                                aria-label={t("announcement.addNew")}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                            </Button>
                        </Can>
                    </WidgetActions>
                </WidgetHeader>

                <WidgetContent className="gap-0 overflow-y-auto p-0">
                    {isError ? (
                        <p className="text-chameleon-pink px-5 py-4">
                            {t("common.errorLoadingData")}
                        </p>
                    ) : isPending ? (
                        <div className="flex flex-col gap-2 p-4">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <Skeleton key={i} className="h-12 w-full" />
                            ))}
                        </div>
                    ) : announcements.length === 0 ? (
                        <p className="text-label text-compendia-muted px-4 py-8 text-center">
                            {t("announcement.noAnnouncements")}
                        </p>
                    ) : (
                        <div className="flex flex-col">
                            {announcements.map((announcement) => (
                                <AnnouncementItem
                                    key={announcement.id}
                                    announcement={announcement}
                                    onClick={(a) => setSelectedAnnouncementId(a.id)}
                                />
                            ))}
                        </div>
                    )}
                </WidgetContent>
            </Widget>

            {showAdd && <AddEditAnnouncementModal open={showAdd} onOpenChange={setShowAdd} />}

            {selectedAnnouncement && (
                <ViewAnnouncementModal
                    open={!!selectedAnnouncement}
                    onOpenChange={(v) => {
                        if (!v) setSelectedAnnouncementId(null);
                    }}
                    announcement={selectedAnnouncement}
                />
            )}
        </>
    );
}
